// NoticeBoardAdd.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useState } from "react";
import BTEditor from "../../common/components/board/BTEditor";
import BTBox from "../../common/components/BTBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import { baseStore } from "../../store/BaseStore";
import { authStore } from "../../store/AuthStore";
import { dialogStore } from "../../store/DialogStore";
import {
  requestCreateNoticeDocuments,
  requestUpdateNoticeDocuments,
  requestUploadFiles,
} from "../../service/BoardService";
import Notice from "../../model/board/Notice";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";

// 오늘 날짜(YYYY-MM-DD)를 서울 시간대 기준으로 계산
const getTodayInSeoul = () => {
  const now = new Date();
  const seoulNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const y = seoulNow.getFullYear();
  const m = String(seoulNow.getMonth() + 1).padStart(2, '0');
  const d = String(seoulNow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const NoticeBoardAdd = () => {
  return (
    <MainLayout>
      <NoticeBoardAddBody />
    </MainLayout>
  );
};

export default NoticeBoardAdd;

const NoticeBoardAddBody = observer(() => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [contents, setContents] = useState("");
  const [postDate, setPostDate] = useState(() => getTodayInSeoul());
  const [isPublished, setIsPublished] = useState(true);
  const [isAnchored, setIsAnchored] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);

  const handleSave = async () => {
    try {
      baseStore.showLoading();
      const now = new Date();

      const notice: Notice = {
        title,
        contents: "",
        postDate: new Date(postDate),
        isPublished,
        isAnchored,
        attachedFiles: [],
        createdBy: authStore.user?.username ?? "",
        createdAt: now,
        updatedBy: authStore.user?.username ?? "",
        updatedAt: now,
      };

      // 1) 먼저 문서 생성
      const docId = await requestCreateNoticeDocuments(notice);
      notice.id = docId;

      // 2) 에디터 내용에서 base64 이미지를 업로드 URL로 치환
      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, "text/html");
      const imgElements = docParsed.querySelectorAll("img");

      const imagesToUpload: File[] = [];
      const base64ImageSrcs: string[] = [];
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) base64ImageSrcs.push(src);
      });

      for (const base64Src of base64ImageSrcs) {
        const res = await fetch(base64Src);
        const blob = await res.blob();
        const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.png`;
        imagesToUpload.push(new File([blob], fileName, { type: blob.type }));
      }

      const uploadedImageUrls =
        imagesToUpload.length > 0
          ? await requestUploadFiles("notice", docId, "image", imagesToUpload)
          : [];

      let idx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) {
          const newSrc = uploadedImageUrls[idx++];
          if (newSrc) img.setAttribute("src", newSrc);
          if (selectedImage === src) setSelectedImage(newSrc);
        }
      });

      // 3) 첨부파일 업로드
      const uploadedAttachmentUrls =
        uploadedFiles.length > 0
          ? await requestUploadFiles("notice", docId, "attachments", uploadedFiles)
          : [];

           
      const imgElements2 = docParsed.querySelectorAll("img");
      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });
      
      // 최종적으로 contents에 반영
      notice.attachedFiles = uploadedAttachmentUrls;
      notice.contents = docParsed.body.innerHTML;

      await requestUpdateNoticeDocuments(notice);

      dialogStore.showDialog({
        title: "성공",
        message: "공지사항이 성공적으로 등록되었습니다.",
        onConfirm: () => navigate(-1),
        onClose: () => navigate(-1),
      });
    } catch (error) {
      dialogStore.showDialog({
        title: "오류",
        message: "공지사항 등록 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>공지사항 작성</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션 타이틀 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>공지사항 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 게시일자 | 글 공개 여부 | 상단 고정 여부 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>게시일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={postDate} onChange={setPostDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>상단 고정 여부</b>
          <BTCheckBox title="" checked={isAnchored} onChange={(e) => setIsAnchored(e.target.checked)} />
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />
        <TitleMediumBold style={{ marginTop: "20px" }}>공지사항 컨텐츠 입력</TitleMediumBold>
        <BTBox height="16px" />

        <BTEditor
          title={title}
          contents={contents}
          selectedImage={selectedImage}
          uploadedFiles={uploadedFiles}
          uploadedFileUrls={uploadedFileUrls}
          setTitle={setTitle}
          setContents={setContents}
          setSelectedImage={setSelectedImage}
          setUploadedFiles={setUploadedFiles}
          setUploadedFileUrls={setUploadedFileUrls}
          onSave={handleSave}
          onCancel={() => navigate(-1)}
          attachmentAccept="all"
        />
      </VerticalLayout>
    </VerticalLayout>
  );
});